// src/pages/VerifyEmailPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import { sendVerificationEmail, friendlyAuthError } from '../utils/authEmail';
import BrandName from '../components/BrandName';
import SpamFolderWarning from '../components/SpamFolderWarning';
import './AuthPages.css';

const RESEND_COOLDOWN_S = 60;
const POLL_MS = 5000;

const VerifyEmailPage = () => {
  useEffect(() => { document.title = 'Verify Email - The WasteGame'; }, []);
  const { user, refreshEmailVerified, logout, verificationSendError, setVerificationSendError } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking]   = useState(false);
  const [notYet, setNotYet]       = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendErr, setResendErr] = useState('');
  const [cooldown, setCooldown]   = useState(0);
  const checkingRef = useRef(false);

  const goToDashboard = useCallback(() => {
    navigate(user?.type === 'provider' ? '/provider-dashboard' : '/customer-dashboard', { replace: true });
  }, [navigate, user?.type]);

  // Reload the Firebase user and move on as soon as the email is verified.
  const checkVerified = useCallback(async (manual = false) => {
    if (!auth.currentUser || checkingRef.current) return;
    checkingRef.current = true;
    if (manual) { setChecking(true); setNotYet(false); }
    try {
      const verified = await refreshEmailVerified();
      if (verified) { goToDashboard(); return; }
      if (manual) setNotYet(true);
    } catch (err) {
      console.warn('[VerifyEmail] reload failed:', err.code, err.message);
      if (manual) setResendErr(friendlyAuthError(err));
    } finally {
      checkingRef.current = false;
      if (manual) setChecking(false);
    }
  }, [refreshEmailVerified, goToDashboard]);

  // Already verified (e.g. came back from the email link) -> straight on.
  useEffect(() => {
    if (user?.emailVerified === true) goToDashboard();
  }, [user?.emailVerified, goToDashboard]);

  // Auto-check every 5s, and whenever the user returns to this tab/app.
  useEffect(() => {
    if (!user || user.emailVerified) return undefined;
    const id = setInterval(() => { if (!document.hidden) checkVerified(false); }, POLL_MS);
    const onFocus = () => checkVerified(false);
    const onVisible = () => { if (!document.hidden) checkVerified(false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, checkVerified]);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!user) return <Navigate to="/login" replace />;

  const handleResend = async () => {
    if (!auth.currentUser || cooldown > 0) return;
    setResending(true);
    setResendMsg('');
    setResendErr('');
    try {
      await sendVerificationEmail(auth.currentUser);
      setVerificationSendError('');
      setResendMsg(`Verification email sent to ${user.email}. It can take a couple of minutes - check spam/junk too.`);
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      console.error('[VerifyEmail] resend failed:', err.code, err.message);
      setResendErr(friendlyAuthError(err));
      if (err.code === 'auth/too-many-requests') setCooldown(RESEND_COOLDOWN_S);
    }
    setResending(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <div className="auth-logo"><BrandName /></div>
        <SpamFolderWarning />
        <div style={{ fontSize: '2.5rem', margin: '8px 0' }}>📧</div>
        <h2>Check your email</h2>

        {verificationSendError ? (
          <div className="auth-error">
            We couldn't send your verification email to <strong>{user.email}</strong>: {verificationSendError}{' '}
            Please use "Resend verification email" below.
          </div>
        ) : (
          <p className="auth-sub">
            We've sent a verification link to <strong>{user.email}</strong>.
            Click the link in that email - this page will continue automatically once you're verified.
          </p>
        )}

        {notYet && (
          <div className="auth-error">
            Not verified yet - please click the link in your verification email. Check spam/junk if you can't find it.
          </div>
        )}
        {resendMsg && <div className="reset-success">{resendMsg}</div>}
        {resendErr && <div className="auth-error">{resendErr}</div>}

        <button
          className="btn btn-primary btn-full"
          onClick={() => checkVerified(true)}
          disabled={checking}
          style={{ marginTop: '8px' }}
        >
          {checking ? 'CHECKING...' : "I've verified my email - Continue"}
        </button>

        <button
          className="btn btn-secondary btn-full"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          style={{ marginTop: '10px' }}
        >
          {resending ? 'SENDING...' : cooldown > 0 ? `Resend available in ${cooldown}s` : 'Resend verification email'}
        </button>

        <p className="auth-footer" style={{ marginTop: '20px' }}>
          Wrong email address?{' '}
          <button
            className="forgot-link"
            style={{ display: 'inline', width: 'auto', textAlign: 'center' }}
            onClick={handleLogout}
          >
            Log out
          </button>{' '}
          and sign up again with the right one.
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
